import React from 'react'
import { Modal, Button } from 'react-bootstrap'
import { SandboxInfo } from 'hooks/use-validate-hubspot-deal'

interface SandboxModalsProps {
  // Phone Preference Modal (Infrastructure)
  showPhonePreferenceModal: boolean
  onHidePhonePreferenceModal: () => void
  onPhonePreferenceSelect: (preference: 'same' | 'different') => void

  // Infrastructure No Phone Confirm
  showInfraNoPhoneConfirm: boolean
  onHideInfraNoPhoneConfirm: () => void
  onInfraNoPhoneConfirm: () => void
  sandboxStatus?: string

  // Sales Enablement Phone Preference
  showSalesEnablementConfirm: boolean
  onHideSalesEnablementConfirm: () => void
  onSalesEnablementPhonePreference: (preference: 'same' | 'different') => void

  // Sales Enablement No Phone Confirm
  showSalesEnablementNoPhoneConfirm: boolean
  onHideSalesEnablementNoPhoneConfirm: () => void
  onSalesEnablementNoPhoneConfirm: () => void

  // Phone Selection Modal
  showPhoneSelectionModal: boolean
  onHidePhoneSelectionModal: () => void
  onPhoneSelection: (selectedSandboxId: number | null) => void
  sandboxInfo: SandboxInfo | null
  usePhoneFromSandboxId: number | null
  setUsePhoneFromSandboxId: (id: number | null) => void
  siblingSandboxesWithPhones: SandboxInfo[]
}

const SandboxModals: React.FC<SandboxModalsProps> = ({
  showPhonePreferenceModal,
  onHidePhonePreferenceModal,
  onPhonePreferenceSelect,
  showInfraNoPhoneConfirm,
  onHideInfraNoPhoneConfirm,
  onInfraNoPhoneConfirm,
  sandboxStatus,
  showSalesEnablementConfirm,
  onHideSalesEnablementConfirm,
  onSalesEnablementPhonePreference,
  showSalesEnablementNoPhoneConfirm,
  onHideSalesEnablementNoPhoneConfirm,
  onSalesEnablementNoPhoneConfirm,
  showPhoneSelectionModal,
  onHidePhoneSelectionModal,
  onPhoneSelection,
  sandboxInfo,
  usePhoneFromSandboxId,
  setUsePhoneFromSandboxId,
  siblingSandboxesWithPhones,
}) => {
  return (
    <>
      {/* Phone Preference Modal (Sandbox - Infrastructure) */}
      <Modal
        show={showPhonePreferenceModal}
        onHide={onHidePhonePreferenceModal}
        centered
        contentClassName="bg-dark text-white"
      >
        <Modal.Header closeButton style={{ borderBottom: '1px solid #4a5568' }}>
          <Modal.Title style={{ fontSize: '18px', fontWeight: 600 }}>
            Phone Number Preference
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ color: '#a0aec0', marginBottom: '1.5rem' }}>
            This customer has an existing sandbox phone number. How would you
            like to handle it for their infrastructure subscription?
          </p>
          <div className="d-flex flex-column gap-3">
            <Button
              variant="outline-light"
              className="text-start p-3"
              onClick={() => onPhonePreferenceSelect('same')}
            >
              <div className="fw-bold">Keep existing number</div>
              <small className="text-muted">
                The current sandbox phone number will be retained
              </small>
            </Button>
            <Button
              variant="outline-light"
              className="text-start p-3"
              onClick={() => onPhonePreferenceSelect('different')}
            >
              <div className="fw-bold">Request a new number</div>
              <small className="text-muted">
                The sandbox number will be returned to the pool and a new number
                assigned
              </small>
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Infrastructure Confirmation Modal (Sandbox - phone already returned) */}
      <Modal
        show={showInfraNoPhoneConfirm}
        onHide={onHideInfraNoPhoneConfirm}
        centered
        contentClassName="bg-dark text-white"
      >
        <Modal.Header closeButton style={{ borderBottom: '1px solid #4a5568' }}>
          <Modal.Title style={{ fontSize: '18px', fontWeight: 600 }}>
            Infrastructure Confirmation
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div
            className="p-3 rounded mb-3"
            style={{ backgroundColor: 'rgba(12, 165, 233, 0.1)' }}
          >
            <div className="d-flex align-items-start gap-2">
              <ion-icon
                name="information-circle"
                style={{ color: '#0ca5e9', fontSize: '20px', marginTop: '2px' }}
              />
              <div>
                <p className="mb-0" style={{ color: '#a0aec0' }}>
                  {sandboxStatus === 'queued'
                    ? 'This was a queued sandbox user — no phone was ever assigned. A new phone number will be assigned to them.'
                    : 'This customer\u2019s sandbox phone number is no longer available. A new phone number will be assigned to them.'}
                </p>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid #4a5568' }}>
          <Button
            variant="outline-secondary"
            onClick={onHideInfraNoPhoneConfirm}
          >
            Cancel
          </Button>
          <Button
            style={{ backgroundColor: '#0ca5e9', borderColor: '#0ca5e9' }}
            onClick={onInfraNoPhoneConfirm}
          >
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Sales Enablement Phone Preference Modal (Sandbox - has phone) */}
      <Modal
        show={showSalesEnablementConfirm}
        onHide={onHideSalesEnablementConfirm}
        centered
        contentClassName="bg-dark text-white"
      >
        <Modal.Header closeButton style={{ borderBottom: '1px solid #4a5568' }}>
          <Modal.Title style={{ fontSize: '18px', fontWeight: 600 }}>
            Phone Number Preference
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ color: '#a0aec0', marginBottom: '1.5rem' }}>
            This customer has an existing sandbox phone number. How would you
            like to handle it for their sales enablement subscription?
          </p>
          <div className="d-flex flex-column gap-3">
            <Button
              variant="outline-light"
              className="text-start p-3"
              onClick={() => onSalesEnablementPhonePreference('same')}
            >
              <div className="fw-bold">Keep existing number</div>
              <small className="text-muted">
                The current sandbox phone number will be retained
              </small>
            </Button>
            <Button
              variant="outline-light"
              className="text-start p-3"
              onClick={() => onSalesEnablementPhonePreference('different')}
            >
              <div className="fw-bold">Request a new number</div>
              <small className="text-muted">
                The sandbox number will be returned to the pool and a new number
                assigned
              </small>
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Sales Enablement Confirmation Modal (Sandbox - phone already gone) */}
      <Modal
        show={showSalesEnablementNoPhoneConfirm}
        onHide={onHideSalesEnablementNoPhoneConfirm}
        centered
        contentClassName="bg-dark text-white"
      >
        <Modal.Header closeButton style={{ borderBottom: '1px solid #4a5568' }}>
          <Modal.Title style={{ fontSize: '18px', fontWeight: 600 }}>
            Sales Enablement Confirmation
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div
            className="p-3 rounded mb-3"
            style={{ backgroundColor: 'rgba(37, 99, 235, 0.1)' }}
          >
            <div className="d-flex align-items-start gap-2">
              <ion-icon
                name="information-circle"
                style={{ color: '#2563eb', fontSize: '20px', marginTop: '2px' }}
              />
              <div>
                <p className="mb-0" style={{ color: '#a0aec0' }}>
                  {sandboxStatus === 'queued'
                    ? 'This was a queued sandbox user — no phone was ever assigned. A new phone number will be assigned and their partner integration will be deleted.'
                    : 'This customer\u2019s sandbox phone number is no longer available. A new phone number will be assigned and their partner integration will be deleted.'}
                </p>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid #4a5568' }}>
          <Button
            variant="outline-secondary"
            onClick={onHideSalesEnablementNoPhoneConfirm}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={onSalesEnablementNoPhoneConfirm}>
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Phone Selection Modal (Sandbox - pick own or sibling phone) */}
      <Modal
        show={showPhoneSelectionModal}
        onHide={onHidePhoneSelectionModal}
        centered
        contentClassName="bg-dark text-white"
      >
        <Modal.Header closeButton style={{ borderBottom: '1px solid #4a5568' }}>
          <Modal.Title style={{ fontSize: '18px', fontWeight: 600 }}>
            Select Phone Number
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ color: '#a0aec0', marginBottom: '1rem' }}>
            Choose which phone number to assign to this customer:
          </p>
          <div className="d-flex flex-column gap-2">
            {/* Own phone - pre-selected */}
            <button
              type="button"
              className="text-start text-white p-3"
              style={{
                backgroundColor: !usePhoneFromSandboxId
                  ? 'rgba(12, 165, 233, 0.15)'
                  : 'transparent',
                border: `1px solid ${
                  !usePhoneFromSandboxId ? '#0ca5e9' : '#4a5568'
                }`,
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                if (usePhoneFromSandboxId) {
                  e.currentTarget.style.backgroundColor =
                    'rgba(12, 165, 233, 0.1)'
                  e.currentTarget.style.borderColor = '#0ca5e9'
                }
              }}
              onMouseLeave={(e) => {
                if (usePhoneFromSandboxId) {
                  e.currentTarget.style.backgroundColor = 'transparent'
                  e.currentTarget.style.borderColor = '#4a5568'
                }
              }}
              onClick={() => setUsePhoneFromSandboxId(null)}
            >
              <div className="fw-bold">
                {sandboxInfo?.phone_number || 'Current number'}
              </div>
              <small style={{ color: '#a0aec0' }}>
                {sandboxInfo?.email} (current)
              </small>
            </button>

            {/* Sibling phones */}
            {siblingSandboxesWithPhones.length > 0 && (
              <div
                style={{
                  fontSize: '12px',
                  color: '#a0aec0',
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  marginTop: '0.5rem',
                  marginBottom: '0.25rem',
                }}
              >
                Other numbers from same domain
              </div>
            )}
            {siblingSandboxesWithPhones.map((sibling) => (
              <button
                key={sibling.id}
                type="button"
                className="text-start text-white p-3"
                style={{
                  backgroundColor:
                    usePhoneFromSandboxId === sibling.id
                      ? 'rgba(12, 165, 233, 0.15)'
                      : 'transparent',
                  border: `1px solid ${
                    usePhoneFromSandboxId === sibling.id ? '#0ca5e9' : '#4a5568'
                  }`,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  if (usePhoneFromSandboxId !== sibling.id) {
                    e.currentTarget.style.backgroundColor =
                      'rgba(12, 165, 233, 0.1)'
                    e.currentTarget.style.borderColor = '#0ca5e9'
                  }
                }}
                onMouseLeave={(e) => {
                  if (usePhoneFromSandboxId !== sibling.id) {
                    e.currentTarget.style.backgroundColor = 'transparent'
                    e.currentTarget.style.borderColor = '#4a5568'
                  }
                }}
                onClick={() => setUsePhoneFromSandboxId(sibling.id)}
              >
                <div className="fw-bold">{sibling.phone_number}</div>
                <small style={{ color: '#a0aec0' }}>{sibling.email}</small>
              </button>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid #4a5568' }}>
          <Button
            variant="outline-secondary"
            onClick={onHidePhoneSelectionModal}
          >
            Cancel
          </Button>
          <Button
            style={{ backgroundColor: '#0ca5e9', borderColor: '#0ca5e9' }}
            onClick={() => onPhoneSelection(usePhoneFromSandboxId)}
          >
            Continue
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default SandboxModals
